import { v4 as uuidv4 } from 'uuid';
import { db } from '@/db';
import type { UploadQueueRow, UploadOptions } from '@/db';


/**
 * Add a file to the upload queue.
 * Returns the generated uuid id.
 */
export async function enqueueFile(file: File, options: UploadOptions): Promise<string> {
  const id = uuidv4();
  const now = Date.now();
  await db.uploadQueue.add({
    id,
    file,
    fileName: file.name,
    fileSize: file.size,
    contentType: file.type,
    status: 'queued',
    progress: 0,
    retries: 0,
    options,
    createdAt: now,
    updatedAt: now,
  });
  return id;
}

/**
 * List queue items, optionally filtered by status.
 */
export async function listQueue(filter?: { status?: UploadQueueRow['status'] }): Promise<UploadQueueRow[]> {
  if (filter?.status) {
    return db.uploadQueue.where('status').equals(filter.status).toArray();
  }
  return db.uploadQueue.orderBy('createdAt').toArray();
}

/**
 * Apply a partial update to a queue row.
 */
export async function updateStatus(id: string, patch: Partial<UploadQueueRow>): Promise<void> {
  await db.uploadQueue.update(id, { ...patch, updatedAt: Date.now() });
}

/**
 * Delete all 'done' items from the queue. User-driven action — clears the
 * "ready to publish" rows immediately, regardless of how long ago they finished.
 * Returns the number of rows deleted.
 */
export async function clearDone(): Promise<number> {
  const toDelete = await db.uploadQueue
    .where('status')
    .equals('done')
    .primaryKeys();
  await db.uploadQueue.bulkDelete(toDelete as string[]);
  return toDelete.length;
}

/**
 * Delete a single queue row by id.
 */
export async function removeItem(id: string): Promise<void> {
  await db.uploadQueue.delete(id);
}

/**
 * Re-queue an errored item for retry (status='queued', retries++).
 */
export async function retryError(id: string): Promise<void> {
  const row = await db.uploadQueue.get(id);
  if (!row) return;
  // exactOptionalPropertyTypes: use db.uploadQueue.update with a modifier that
  // removes the error key rather than setting it to undefined.
  await db.uploadQueue.update(id, {
    status: 'queued',
    retries: row.retries + 1,
    updatedAt: Date.now(),
  });
  // Remove the error field (cannot set optional field to undefined in strict mode)
  await db.uploadQueue.where('id').equals(id).modify((item) => {
    delete item.error;
  });
}
